import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, ListCell, NameCell, NumberCell } from '@/components/TableCells'
import { Ruleset } from '@/services/rules/rulesetsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  type: 'type',
  objectState: 'objectState',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface RulesetTableProps {
  rulesets: Ruleset[]
  loading?: boolean
  emptyMessage?: string
  selection?: Ruleset[]
  onSelectionChange?: (rulesets: Ruleset[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function RulesetTable({
  rulesets,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: RulesetTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<Ruleset, unknown>[] = [
    ...(selectable ? [selectionColumn<Ruleset>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'type',
      header: t('common.type'),
      size: 130,
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.type}
        </span>
      ),
    },
    {
      accessorKey: 'floorPrice',
      header: t('rulesets.floorPrice'),
      enableSorting: false,
      size: 130,
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.floorPrice ? `${row.original.floorPrice.amount} ${row.original.floorPrice.currency}` : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    },
    optionalColumn<Ruleset>({
      id: 'targetingTypes',
      header: t('rulesets.targetingTypes'),
      enableSorting: false,
      meta: { minWidth: 170 },
      cell: ({ row }) => <ListCell values={row.original.targetingTypes} />,
    }),
    optionalColumn<Ruleset>({
      id: 'sizes',
      header: t('rulesets.sizes'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.sizes?.map((s) => s.dimensions.join('×'))} />,
    }),
    optionalColumn<Ruleset>({
      id: 'layouts',
      header: t('rulesets.layouts'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.layouts?.map((l) => l.name)} />,
    }),
    optionalColumn<Ruleset>({
      id: 'teams',
      header: t('common.teams'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.teams?.map((team) => team.name)} />,
    }),
    optionalColumn<Ruleset>({
      id: 'advertiserFloorPrices',
      header: t('rulesets.advertiserFloorPrices'),
      accessorFn: (row) => row.advertiserFloorPrices?.length ?? 0,
      size: 160,
      cell: ({ row }) => <NumberCell value={row.original.advertiserFloorPrices?.length ?? 0} />,
    }),
    optionalColumn<Ruleset>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<Ruleset>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={rulesets}
      loading={loading}
      emptyMessage={emptyMessage ?? 'No rulesets found.'}
      columnsKey="rulesets"
      onRowClick={(row) => navigate(`/publishing/rulesets/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
